import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, Application, Dashboard, IGridsterOptions, DefaultGridsterOptions, Workspace, ApplicationDto } from '../../../models';
import {
    IApplicationService,
    IAuthorizationService,
    IDashboardFactory,
    IUserSettingsService,
    IStoreService,
    IRequestService,
    IRolesService,
    ITranslatedLoggerService
} from '../../../services';
import { IRootScope } from 'IRootScope';
import { IWidget } from '../../../widgets';
import { isEditMode, applicationCreated, applicationSaved, applicationsFavorites, togglePropertiesMode, toggleEditMode } from '../../../common';
import { DatesHelper } from '../../../common/datesHelper';
import { ApplicationViewModel } from '../applicationViewModel';
import { StoreElement } from '../../../stateManagement';

class WorkspaceStateController implements angular.IComponentController {
    static errorSaveMessage = 'Save or cancel changes before selecting another workspace.';

    allApplications: Application[];
    formController: angular.IFormController;
    gridsterOptions: IGridsterOptions;
    isChecking: boolean;
    isEditMode: boolean;
    isLoading: boolean;
    isSuperAdmin: boolean;
    message: string;
    selectedItem: IWidget;
    tuples: ApplicationViewModel[];
    widgetList: IWidget[] = [];

    private _canceller: angular.IDeferred<void>;
    private _userName: string;
    private _unsubscribe: () => void;

    static $inject = [
        '$scope',
        '$rootScope',
        '$q',
        '$state',
        'requestService',
        'storeService',
        'applicationService',
        'authorizationService',
        'dashboardFactory',
        'userSettingsService',
        'rolesService',
        'translatedLoggerService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$q: angular.IQService,
        private _$state: angular.ui.IStateService,
        requestService: IRequestService,
        private _storeService: IStoreService,
        private _applicationService: IApplicationService,
        private _authorizationService: IAuthorizationService,
        private _dashboardFactory: IDashboardFactory,
        private _userSettingsService: IUserSettingsService,
        private _rolesService: IRolesService,
        private _translatedLoggerService: ITranslatedLoggerService
    ) {
        // call this before any other init is preferable
        requestService.cancelPendingRequest();
    }

    $onInit() {
        this.isLoading = true;
        this.isEditMode = false;
        this.gridsterOptions = angular.copy(DefaultGridsterOptions);
        this._storeService.dispatchers().authentication.disconnect();

        const storeLoaded = this._$q.defer();
        // retrieve current user's username
        this._unsubscribe = this._storeService.register(
            (store) => {
                const auth = store.authentication.value;
                this._userName = auth.userName;
                this.isSuperAdmin = auth.isSuperAdmin;
                if (this._userName) {
                    storeLoaded.resolve();
                }
                const appName = store.application?.value?.name ?? 'WideTech - WiDE';
                document.title = `V2 - ${appName}`;
            },
            [StoreElement.Authentication, StoreElement.Application]
        );

        this._$scope.$on(toggleEditMode, () => this.setEditMode(this._$rootScope.isEditMode));
        this._$scope.$on(isEditMode, () => this.onIsEditMode(false, false));
        this._$scope.$on(applicationSaved, (ev, data) => this.refreshOne(data));
        this._$scope.$on(applicationCreated, () => this.refreshAll(true));
        storeLoaded.promise.then(() => this.refreshAll());
    }

    $onDestroy() {
        this._unsubscribe();
        this.cancelRequest();
    }

    private refreshAll(force: boolean = false) {
        this.refreshApplication();
        this._applicationService.getAll((data) => (this.allApplications = data.map((d) => d.application)), force);
    }

    private refreshOne(app: Application): angular.IPromise<void> {
        if (app) {
            const found = _.find(this.tuples, (t) => t.item1.guid === app.guid);
            if (!found) {
                this.refreshApplication();
            } else {
                found.item2 = undefined;
                return this._applicationService
                    .getOneWithInformation(this._userName, app.guid, this._canceller.promise)
                    .then((data) => {
                        found.item2 = data.item2;
                        this.computeErrors(found);
                        if (this.allApplications) {
                            const newList = this.allApplications
                                .map((d) => {
                                    if (d.guid === data.item1.guid) {
                                        return data.item1;
                                    } else {
                                        return d;
                                    }
                                })
                                .sort((a, b) => (a.name > b.name ? 1 : -1));
                            this.allApplications = newList;
                        }
                    })
                    .catch((reason) => {
                        this._translatedLoggerService.logError('Cannot refresh selected application.');
                    })
                    .finally(() => {
                        this.updateVmAfterAdd(app);
                    });
            }
        }
    }

    private refreshApplication() {
        this.isLoading = true;
        this.message = 'Retrieving applications';

        this._$q
            .all([this._rolesService.getAll(), this._applicationService.getForUserAsync(this._userName, false)])
            .then((responses) => {
                const roles: { [id: string]: string } = {};
                responses[0].forEach((r) => (roles[r.id] = r.name));
                const dtos = responses[1];
                const favourites = this.getFavorites();
                const work = dtos.map((d) => {
                    return {
                        item1: d.application,
                        isFavourite: favourites.indexOf(d.application.guid) >= 0,
                        currentUserRoles: d.roles[this._userName].map((r) => roles[r])
                    } as any;
                });
                this.tuples = _.orderBy(work, ['isFavourite'], ['desc']);
                this.isLoading = false;
                this.isChecking = true;
                this._canceller = this._$q.defer<void>();

                this.isSingleRedirect(dtos);

                return this._applicationService.getWithInformationForUser(this._userName, this._canceller.promise);
            })
            .then((data) => {
                const favourites = this.getFavorites();
                const work = this.tuples.map((d) => {
                    const newItem = _.find(data, (dd) => dd.item1.guid === d.item1.guid);
                    if (newItem) {
                        d.item2 = newItem.item2;
                    }
                    d.isFavourite = favourites.indexOf(d.item1.guid) >= 0;
                    this.computeErrors(d);
                    return d;
                });
                this.tuples = _.orderBy(work, ['isFavourite'], ['desc']);
            })
            .catch((reason) => {
                if (this._canceller && (!reason.xhrStatus || reason.xhrStatus !== 'abort')) {
                    console.log(reason);
                    this._translatedLoggerService.logError('Cannot retrieve the applications list.');
                }
            })
            .finally(() => {
                this.isLoading = false;
                this.isChecking = false;
            });
    }

    private computeErrors(d: ApplicationViewModel): void {
        const vm = d.item2;

        if (vm.isInvalid) {
            d.hasError = true;
            d.message = 'Application is corrupted. You should restore a backup.';
        } else if (vm.hasFailedJob) {
            d.hasError = true;
            d.message = 'Online job failed to start and needs to be restarted by authorized user (administrator, engineer or master)';
        } else if (vm.hasDelayedJobs) {
            d.hasError = true;
            d.message = 'Delayed jobs';
        } else if (vm.hasFailedTasks) {
            d.hasError = true;
            d.message = 'Failed tasks';
        } else if (vm.inactiveJobsCount > 0) {
            d.hasWarning = true;
            d.message = 'Inactive jobs';
        } else if (vm.disabledEmailCount > 0) {
            d.hasWarning = true;
            d.message = 'Disabled emails';
        } else if (vm.logErrorsCount > 0) {
            d.hasWarning = true;
            d.message = 'Error in logs';
        } else if (vm.hasDeprecatedConnectionPoints) {
            d.hasWarning = true;
            d.message = 'PI SDK will be deprecated in the future. Consider using PI AF instead';
        } else if (d.item1.ignoreErrors) {
            d.hasError = false;
            d.hasWarning = false;
            d.message = 'Ignore errors';
        }
    }

    isSingleRedirect(data: ApplicationDto[]): boolean {
        let result = false;

        if (data.length === 0) {
            this._translatedLoggerService.logWarning('No applications defined or authorized for _userName_.', undefined, { userName: this._userName });
            result = true;
        } else if (data.length === 1 && !this._authorizationService.isConnected() && !this.isSuperAdmin) {
            result = true;
            const app = data[0].application;
            const workspace: Workspace = {
                application: app.name,
                environment: 'Development',
                model: 'Model'
            };
            this.message = `Connecting to application ${workspace.application}`;
            this.isLoading = true;
            this._storeService
                .dispatchers()
                .authentication.connect(this._userName, workspace, app.guid, true)
                .then(() => {
                    this.cancelRequest();
                    this.isLoading = false;
                    this._storeService.dispatchers().application.connect(app);
                    this._$state.go('home');
                });
        }

        return result;
    }

    drop(droppedData) {
        // prevent drop with file or something else
        if (!droppedData || !droppedData.itemId) {
            return;
        }
        if (droppedData.itemId === 'addWorkspace') {
            this.isLoading = true;
            this.createApplication().finally(() => (this.isLoading = false));
        } else if (droppedData.itemId === 'importApplication') {
            $('#importApplicationModal').modal('show');
        }
    }

    private cancelRequest() {
        if (this._canceller) {
            this._canceller.resolve();
        }
    }

    private createApplication(): angular.IPromise<void> {
        const application = {
            name: 'New application',
            olsonTimeZoneName: new DatesHelper().getOlsonName('Local')
        };

        return this._applicationService
            .add(application)
            .then((data: any) => {
                const application = data.item1;
                this.allApplications = this.allApplications.concat([application]);
                let tuples = angular.copy(this.tuples);
                tuples.push(data);
                tuples = _.sortBy(tuples, (t) => t.item1.name);
                this.tuples = tuples;
                this.selectedItem = data;
                const listener = this._$scope.$watch(() => {
                    const elm = document.getElementById(`item${application.guid}`);
                    if (elm) {
                        elm.scrollIntoView();
                        listener();
                    }
                });
                // create 4 default dashboards
                return this.createDefaultDashboards(application);
            })
            .then(() => {
                this._translatedLoggerService.logSuccess('Application _applicationName_ has been created sucessfully.', undefined, {
                    applicationName: application.name
                });
            })
            .catch((reason) => {
                this._translatedLoggerService.logError(reason);
            });
    }

    createDefaultDashboards(application: Application): angular.IPromise<Dashboard[]> {
        const dashboards: Dashboard[] = [];
        dashboards.push(this.createDashboard('missing', 'Missing values', 0));
        dashboards.push(this.createDashboard('badValues', 'Bad tags', 3));
        dashboards.push(this.createDashboard('flat', 'Flat tags', 6));
        dashboards.push(this.createDashboard('scatter', 'Scattered tags', 9));
        dashboards.push(this.createDashboard('outOfRange', 'Out-of-range tags', 12));

        return this._storeService.dispatchers().dashboards.addManyForApplication(dashboards, application.guid, true);
    }

    createDashboard(type: string, name: string, col: number) {
        const dashboard = this._dashboardFactory.createDashboard(type);
        if (dashboard) {
            dashboard.name = name;
            dashboard.col = col;
        }
        return dashboard;
    }

    isSelected(item: ApplicationViewModel) {
        return this.selectedItem && item.item1.guid === (this.selectedItem as any).item1.guid;
    }

    selectItem(item: ApplicationViewModel, $event: angular.IAngularEvent) {
        if (this.isEditMode && !this.isChecking) {
            if (!this.formController || !this.formController.$dirty) {
                if (this.isEditMode) {
                    this.setSelectedItem(item);
                    $event.preventDefault();
                }
            } else {
                this._translatedLoggerService.logWarning(WorkspaceStateController.errorSaveMessage);
                $event.stopPropagation();
            }
        } else {
            //this.connect(item);
        }
    }

    setSelectedItem(item) {
        this.selectedItem = item;
    }

    connect(item: ApplicationViewModel, $event: angular.IAngularEvent): void {
        if (!this.isEditMode) {
            // regenerate workspace on connect if app name has changed
            $event.stopPropagation();
            const workspace: Workspace = {
                application: item.item1.name,
                environment: 'Development',
                model: 'Model'
            };
            if (this._canceller) {
                this._canceller.resolve();
                this._canceller = null;
            }
            this._storeService
                .dispatchers()
                .authentication.connect(this._userName, workspace, item.item1.guid, true)
                .then(() => {
                    this._storeService.dispatchers().application.connect(item.item1);
                    this._$state.go('home');
                });
        }
    }

    setIdle(item: ApplicationViewModel, $event: angular.IAngularEvent): void {
        $event.stopPropagation();
        const newIdle = !item.item1.isIdleMode;
        this._applicationService
            .setIdle(item.item1.guid, newIdle)
            .then(() => {
                const app = angular.copy(item.item1);
                app.isIdleMode = newIdle;
                this.replaceVm(app);
                this._translatedLoggerService.logSuccess('Application successfully updated');
            })
            .catch((reason) => {
                this._translatedLoggerService.logError(reason);
            });
    }

    unselect($event) {
        if (!$event.isDefaultPrevented()) {
            if (!this.formController || !this.formController.$dirty) {
                if (!$event.isDefaultPrevented()) {
                    this.setSelectedItem(null);
                }
            } else {
                this._translatedLoggerService.logWarning(WorkspaceStateController.errorSaveMessage);
                $event.stopPropagation();
            }
        }
    }

    deleteItem(item) {
        item.isDeleting = true;
        this._applicationService
            .delete(item)
            .then(() => {
                this.selectedItem = null;
                _.remove(this.tuples, (t) => t.item1 === item);
                this._translatedLoggerService.logSuccess('Application _applicationName_ deleted.', undefined, { applicationName: item.name });
                this.allApplications = this.allApplications.filter((a) => a.guid !== item.guid);
            })
            .catch(() => {
                this._translatedLoggerService.logError('Failed to delete application.', true);
            })
            .finally(() => {
                item.isDeleting = false;
            });
    }

    private updateVmAfterAdd(app: Application): void {
        if (this.tuples && app) {
            this.allApplications = this.allApplications.concat([app]);
            this.replaceVm(app);
        }
    }

    private replaceVm(app: Application): void {
        const idx = _.findIndex(this.tuples, (t) => t.item1.guid === app.guid);

        if (idx >= 0) {
            const found = this.tuples[idx];
            found.item1 = app;
            found.item2.applicationName = app.name;
            const newFound = angular.copy(found);
            this.tuples[idx] = newFound;
            this.selectedItem = this.tuples[idx] as any;
        }
    }

    getFavorites() {
        const result = this._userSettingsService.getTypedSettingValue<string[]>(applicationsFavorites);
        return result ? result : [];
    }

    onIsEditMode(draggable: boolean = true, resizable: boolean = true) {
        return (event, argument) => {
            if (argument === true) {
                this.gridsterOptions.draggable.enabled = draggable;
                this.gridsterOptions.resizable.enabled = resizable;
            } else {
                this.selectedItem = null;
                this.gridsterOptions.draggable.enabled = false;
                this.gridsterOptions.resizable.enabled = false;
            }
            this.isEditMode = argument;
        };
    }

    togglePropertiesMode(): void {
        if (this._$rootScope) {
            this._$rootScope.$broadcast(togglePropertiesMode);
        } // TODO: should return a error message wheather _rootScope is undefined?
    }

    toggleEditMode() {
        if (this._$rootScope) {
            this._$rootScope.$broadcast(toggleEditMode);
        } // TODO: should return a error message wheather _rootScope is undefined?
    }

    private setEditMode(isEditMode: boolean): void {
        this.isEditMode = isEditMode;
    }
}

export const wtWorkspaceState: ComponentDefinition = {
    componentId: 'wtWorkspaceState',
    controller: WorkspaceStateController,
    template: require('./workspaceState.component.html'),
    controllerAs: 'vm',
    bindings: {}
};
